import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Badge,
  Fade,
  Box,
  Button,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { QK } from "../../base/qk";
import { getAlerts } from "../../api/services/notification";
import { 
  NotificationsActive as NotificationsIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as AccessTimeIcon,
  Circle as CircleIcon,
} from "@mui/icons-material";

const alertSound = new Audio("/alert.mp3");

const LatestAlerts = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [recentAlerts, setRecentAlerts] = useState([]);
  const seenAlertIds = useRef(new Set());
  const isInitialMount = useRef(true);
  const lastFetchTime = useRef(0);

  const { data } = useQuery(QK.latestAlerts, () => getAlerts(1), {
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!data?.results) return;

    const now = Date.now();
    const newAlerts = [];

    data.results.slice(0, 3).forEach((alert) => {
      if (!seenAlertIds.current.has(alert.id)) {
        seenAlertIds.current.add(alert.id);
        newAlerts.push({
          ...alert,
          expiresAt: now + 60000,
          isNew: !isInitialMount.current,
        });
      }
    });

    if (newAlerts.length > 0) {
      if (!isInitialMount.current) {
        alertSound.play();
        setRecentAlerts(newAlerts);
      } else {
        setRecentAlerts(newAlerts.map((alert) => ({ ...alert, isNew: false })));
      }
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

    lastFetchTime.current = now;
  }, [data]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setRecentAlerts((alerts) => alerts.filter((a) => a.expiresAt > now));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleViewAll = () => {
    navigate("/operations?tab=alerts");
  };

  const getAlertColor = (type) => {
    const colors = {
      critical: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
      success: theme.palette.success.main,
    };
    return colors[type?.toLowerCase()] || theme.palette.grey[500];
  };

  return (
    <Card
      sx={{
        height: 320,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2, pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Badge
              color="error"
              variant="dot"
              invisible={recentAlerts.length === 0}
            >
              <NotificationsIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Badge>
            <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
              Latest Alerts
            </Typography>
            {recentAlerts.length > 0 && (
              <Chip
                label={recentAlerts.length}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  color: theme.palette.error.main,
                }}
              />
            )}
          </Box>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            onClick={handleViewAll}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.7rem",
              color: theme.palette.primary.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            View All
          </Button>
        </Box>
      </CardContent>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
        {recentAlerts.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              No new alerts
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center">
              All caught up! 🎉
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ p: 0 }}>
            {recentAlerts.map((alert) => (
              <Fade
                in={true}
                key={alert.id}
                timeout={{ enter: 300, exit: 1000 }}
              >
                <ListItem
                  disablePadding
                  sx={{
                    bgcolor: alert.isNew
                      ? alpha(theme.palette.primary.main, 0.06)
                      : "transparent",
                    borderRadius: 1,
                    mb: 0.5,
                    p: 1,
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                    borderLeft: `3px solid ${getAlertColor(alert.notification_type)}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, width: "100%" }}>
                    <Box sx={{ mt: 0.5 }}>
                      <CircleIcon
                        sx={{
                          fontSize: 8,
                          color: getAlertColor(alert.notification_type),
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={alert.isNew ? 600 : 400}
                        sx={{
                          color: "#1A1A2E",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          fontSize: "0.8rem",
                        }}
                      >
                        {alert.notification_type} - {alert.client_name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                        <AccessTimeIcon sx={{ fontSize: 12, color: "#94A3B8" }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                          {new Date(alert.created_at).toLocaleString()}
                        </Typography>
                        {alert.isNew && (
                          <Chip
                            label="New"
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: "0.5rem",
                              fontWeight: 600,
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                              ml: 0.5,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              </Fade>
            ))}
          </List>
        )}
      </Box>
    </Card>
  );
};

export default LatestAlerts;